/**
 * Key derivation for journal rows and effect idempotency.
 */

export const JOURNAL_KEY = "frdm.tx.journal.v1";
export const LEADER_KEY = "frdm.tx.leader.v1";
export const SCHEMA_VERSION = 1;

/** Rows older than this are unresolvable in practice and get GC'd. */
export const ENTRY_TTL_MS = 24 * 60 * 60 * 1000;

function norm(value) {
  return String(value == null ? "" : value).trim().toLowerCase();
}

/**
 * Identity of one signing step.
 *
 * `instanceId` matters more than it looks: the four `approve → openDispute`
 * screens are rendered per-order, so without it a dispute on order A and a
 * dispute on order B would share a row and clobber each other. Callers pass the
 * order's `_id`. Single-instance screens (withdraw, collateral, stake) omit it.
 *
 * Scoped by `chainId` and `address` so switching account or network simply
 * changes which rows are visible — no clearing, no cross-contamination, and the
 * previous account's flow is intact when you switch back.
 *
 * Step-scoped, never nonce-only: ERC-4337 smart accounts from Reown social
 * login do not bump the EVM nonce, so `approve` and the action that follows it
 * resolve to the SAME nonce. Keying on nonce alone made them collide — see the
 * comment at Collateral.jsx:165.
 */
export function entryKey({ chainId, address, flowId, stepId, instanceId }) {
  return [
    chainId ?? "0",
    norm(address),
    norm(flowId),
    norm(instanceId || "default"),
    norm(stepId),
  ].join("|");
}

/** All rows belonging to one wallet on one chain. */
export function scopeMatches(entry, { chainId, address }) {
  if (!entry) return false;
  return entry.chainId === chainId && norm(entry.address) === norm(address);
}

/**
 * Idempotency key sent with a post-confirmation effect.
 *
 * A plain composite rather than a hash: it is deterministic and unique for the
 * same reasons a hash would be — one transaction can produce at most one effect
 * of a given type — while staying readable in Mongo when someone is debugging a
 * duplicate, and needing no crypto dependency.
 */
export function effectKeyFor(effectType, txHash) {
  return `${norm(effectType)}:${norm(txHash)}`;
}

export function newEntry({
  chainId,
  address,
  flowId,
  stepId,
  instanceId,
  accountType,
  now = Date.now(),
}) {
  return {
    version: SCHEMA_VERSION,
    key: entryKey({ chainId, address, flowId, stepId, instanceId }),
    chainId,
    address: norm(address),
    flowId,
    stepId,
    instanceId: instanceId || "default",
    accountType: accountType || "eoa",
    status: "idle",
    request: null,
    nonceAtSubmit: null,
    hashes: [],
    receipt: null,
    error: null,
    effect: null,
    createdAt: now,
    updatedAt: now,
    expiresAt: now + ENTRY_TTL_MS,
  };
}
